import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Link,
} from "@mui/material";
import {
  createAgentFromIntent,
  getAgent,
  configureAgent,
  updateAgent,
  deleteAgent,
  listIndexes,
  listKeys,
  listTools,
} from "../../api/agentApi";
import { useLocation, useNavigate } from "react-router-dom";
import ExpressAgentReviewOrEdit from "./ExpressAgentReviewOrEdit";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from "@mui/icons-material/EditOutlined";
import { getAgentCreatedDetailState } from "../../agentNavigation";

const extractSelectedToolKey = (tool: any): string | null => {
  const requiredKeyEntry =
    Array.isArray(tool?.required_keys) && tool.required_keys.length > 0
      ? tool.required_keys[0]
      : null;
  const requiredKeyName = requiredKeyEntry ? Object.keys(requiredKeyEntry)[0] : null;
  return requiredKeyName ? requiredKeyEntry[requiredKeyName] : tool?.selectedKey ?? null;
};

const getToolRequiredKeys = (tool: any): any[] => {
  if (Array.isArray(tool?.keys_schema)) return tool.keys_schema;
  return [];
};

const formatToolKeyForAgent = (tool: any) => {
  const keysSchema = getToolRequiredKeys(tool);
  if (tool.tool_id && keysSchema.length > 0 && tool.selectedKey) {
    const keyName = keysSchema[0]?.name;
    return keyName ? [{ [keyName]: tool.selectedKey }] : undefined;
  }
  return undefined;
};

const serializeToolForAgent = (tool: any) => {
  const requiredKeys = formatToolKeyForAgent(tool);
  return {
    ...tool,
    selectedKey: tool.selectedKey || extractSelectedToolKey(tool),
    ...(requiredKeys && { required_keys: requiredKeys }),
  };
};

const IntentToAgentCreator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingAgentCard = location.state?.agentCard;
  const isEdit = !!editingAgentCard;
  const editingAgentId = editingAgentCard?.agent_id;
  // --- STATE ---
  const [activeStep, setActiveStep] = useState(isEdit ? 1 : 0);
  const [intent, setIntent] = useState<string>("");
  const [agentName] = useState<string>("");
  const [provider] = useState<string>("OpenAI");

  const [isDrafting, setIsDrafting] = useState(false);
  const [discoveredTools, setDiscoveredTools] = useState<any[]>([]);
  const [availableKeys, setAvailableKeys] = useState<any[]>([]);
  const [catalogTools, setCatalogTools] = useState<any[]>([]);
  const [agentResponse, setAgentResponse] = useState<any>(null);
  const [hydratedEditingAgentCard, setHydratedEditingAgentCard] = useState<any>(
    editingAgentCard || null,
  );

  const [editableAgentName, setEditableAgentName] = useState<string>("");
  const [editableProvider, setEditableProvider] = useState<string>("OpenAI");
  const [editableRole, setEditableRole] = useState<string>("");
  const [editableDescription, setEditableDescription] = useState<string>("");

  const [docIndexes, setDocIndexes] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState<string | null>(null);
  const [selectedKnowledgeSpaceIds, setSelectedKnowledgeSpaceIds] = useState<string[]>([]);

  const [isCreating, setIsCreating] = useState(false);
  const [pendingAgentId, setPendingAgentId] = useState<string | null>(null); // Track pending agent for manual cleanup
  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    // Fetch document indexes for knowledge base selection
    listIndexes("doc")
      .then((res) => {
        setDocIndexes(res.indexes || []);
      })
      .catch(() => setDocIndexes([]));
  }, []);

  useEffect(() => {
    listKeys()
      .then((res) => setAvailableKeys(res.keys || []))
      .catch(() => setAvailableKeys([]));
  }, []);

  useEffect(() => {
    listTools()
      .then((res) => setCatalogTools(Array.isArray(res) ? res : []))
      .catch(() => setCatalogTools([]));
  }, []);

  const normalizeToolForReview = useCallback((tool: any) => {
    const catalogMatch = catalogTools.find(
      (catalogTool: any) =>
        (tool.tool_id && catalogTool.id === tool.tool_id) ||
        (tool.tool_id && catalogTool.tool_id === tool.tool_id) ||
        (!tool.tool_id && catalogTool.name === tool.name),
    );
    const keysSchema =
      Array.isArray(tool.keys_schema) && tool.keys_schema.length > 0
        ? tool.keys_schema
        : catalogMatch?.keys_schema || [];

    return {
      ...tool,
      tool_id: tool.tool_id || catalogMatch?.id || catalogMatch?.tool_id,
      version: tool.version || catalogMatch?.version,
      display_name: tool.display_name || catalogMatch?.display_name || tool.name,
      description: tool.description || catalogMatch?.description || "",
      keys_schema: keysSchema,
      selectedKey: tool.selectedKey || extractSelectedToolKey(tool),
    };
  }, [catalogTools]);

  useEffect(() => {
    if (!isEdit || !editingAgentCard) {
      setHydratedEditingAgentCard(null);
      return;
    }

    let cancelled = false;
    setHydratedEditingAgentCard(editingAgentCard);

    if (!editingAgentId) return;

    getAgent(editingAgentId)
      .then((agentDetails) => {
        if (cancelled) return;
        setHydratedEditingAgentCard({
          ...editingAgentCard,
          ...agentDetails,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setHydratedEditingAgentCard(editingAgentCard);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, editingAgentCard, editingAgentId]);

  const editAgentCard = hydratedEditingAgentCard || editingAgentCard;

  // Populate edit mode data immediately (except document index and tool keys)
  useEffect(() => {
    if (isEdit && editAgentCard) {
      setEditableAgentName(
        editAgentCard.display_name || editAgentCard.name || "",
      );
      setEditableProvider(editAgentCard.llm || "OpenAI");
      setEditableRole(editAgentCard.role || "");
      setEditableDescription(editAgentCard.description || "");
      setDiscoveredTools((editAgentCard.tools || []).map(normalizeToolForReview));
      setAgentResponse(editAgentCard);
      setPendingAgentId(editAgentCard.agent_id);
    }
  }, [isEdit, editAgentCard, normalizeToolForReview]);

  useEffect(() => {
    if (discoveredTools.length === 0 || catalogTools.length === 0) return;
    setDiscoveredTools((prevTools) => {
      const normalizedTools = prevTools.map(normalizeToolForReview);
      const changed = normalizedTools.some((tool, index) => {
        const previous = prevTools[index];
        return (
          tool.tool_id !== previous.tool_id ||
          tool.version !== previous.version ||
          (tool.keys_schema || []).length !== (previous.keys_schema || []).length ||
          tool.selectedKey !== previous.selectedKey
        );
      });
      return changed ? normalizedTools : prevTools;
    });
  }, [catalogTools, discoveredTools, normalizeToolForReview]);

  // Separate effect to populate document index AFTER docIndexes are loaded
  useEffect(() => {
    if (isEdit && editAgentCard && docIndexes.length > 0) {
      // Set selected document index if available
      // Check multiple possible locations for index data
      const agentIndexes =
        editAgentCard.indexes || editAgentCard.rag || [];

      const knowledgeSpaceEntries = Array.isArray(editAgentCard.knowledge_spaces)
        ? editAgentCard.knowledge_spaces
        : [];
      const ids = [
        ...knowledgeSpaceEntries
          .map((entry: any) => entry.knowledge_space_id || entry.index_id || entry.id || entry)
          .filter(Boolean),
        ...agentIndexes
          .filter((entry: any) => !entry.rag_type || entry.rag_type === "doc")
          .map((entry: any) => entry.index_id || entry.id || entry)
          .filter(Boolean),
      ];
      const uniqueIds = Array.from(new Set(ids.map((id: any) => String(id))));
      setSelectedKnowledgeSpaceIds(uniqueIds);
      setSelectedDocIndex(uniqueIds[0] || null);
    }
  }, [isEdit, editAgentCard, docIndexes]);

  // NO AUTO-CLEANUP: Removed automatic cleanup on unmount
  // Rationale: Auto-cleanup was too aggressive and caused bugs in happy path
  // Users must explicitly use "Cancel & Discard" button to delete pending agents
  // Orphaned pending agents can be cleaned up manually by admins if needed

  const handleDraftIntent = async () => {
    setIsDrafting(true);
    try {
      const payload: any = {
        intent,
        llm_name: provider,
        display_name: agentName,
        tools_collection: "next_moca_tools",
        max_tools: 10,
      };
      const response = await createAgentFromIntent(payload);
      await new Promise((resolve) => setTimeout(resolve, 500));
      const agentDetails = await getAgent(response.agent_id);
      setAgentResponse(agentDetails);
      setPendingAgentId(agentDetails.agent_id); // Track pending agent
      setDiscoveredTools((agentDetails.tools || []).map(normalizeToolForReview));
      setEditableAgentName(
        agentDetails.display_name || agentDetails.name || agentName,
      );
      setEditableProvider(agentDetails.llm || provider);
      setEditableRole(agentDetails.role || "");
      setEditableDescription(agentDetails.description || "");
      setActiveStep(1);
    } catch (error: any) {
      setNotification({
        open: true,
        message: "Analysis failed",
        severity: "error",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  const handleCreateAgent = async () => {
    setIsCreating(true);
    try {
      const updatedTools = discoveredTools.map(serializeToolForAgent);
      if (isEdit) {
        // Edit mode: Use PUT /agent/{id} to create a new version
        const agentCard = {
          name: agentResponse.name,
          display_name: editableAgentName,
          description: editableDescription,
          role: editableRole,
          llm: editableProvider,
          tools: updatedTools,
          document_index_id: selectedKnowledgeSpaceIds[0] || selectedDocIndex,
          knowledge_space_ids: selectedKnowledgeSpaceIds,
        };
        await updateAgent(agentResponse.agent_id, agentCard);
      } else {
        // Create mode: Use PUT /agent/{id}/configure (no new version)
        await configureAgent(
          agentResponse.agent_id,
          updatedTools,
          agentResponse.version,
          editableAgentName,
          editableProvider,
          editableRole,
          editableDescription,
          selectedKnowledgeSpaceIds[0] || selectedDocIndex,
          selectedKnowledgeSpaceIds,
        );
      }

      // Success - navigate to agent detail page
      setPendingAgentId(null);
      if (isEdit) {
        navigate(`/agent/${agentResponse.agent_id}`);
      } else {
        navigate(`/agent/${agentResponse.agent_id}`, {
          state: getAgentCreatedDetailState(agentResponse.agent_id),
        });
      }
    } catch (error: any) {
      setNotification({
        open: true,
        message: `${isEdit ? "Update" : "Build"} failed: ${error.response?.data?.detail || error.message}`,
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleToolKeyChange = (toolIndex: number, key: any | null) => {
    setDiscoveredTools((prevTools) =>
      prevTools.map((tool, index) =>
        index === toolIndex
          ? { ...tool, selectedKey: key?.name || null }
          : tool,
      ),
    );
  };

  const handleCancelCreation = async () => {
    if (isEdit) {
      // In edit mode, just navigate back to agent detail
      navigate(`/agent/${agentResponse.agent_id}`);
      return;
    }

    if (pendingAgentId) {
      try {
        await deleteAgent(pendingAgentId);
        setPendingAgentId(null);
        setAgentResponse(null);
        setDiscoveredTools([]);
        setActiveStep(0);
        setNotification({
          open: true,
          message: "Agent draft discarded",
          severity: "info",
        });
      } catch (error: any) {
        setNotification({
          open: true,
          message: "Failed to discard agent",
          severity: "error",
        });
      }
    } else {
      setActiveStep(0);
    }
  };

  const handleIdentityChange = (
    field: "name" | "provider" | "role" | "description",
    value: string,
  ) => {
    switch (field) {
      case "name":
        setEditableAgentName(value);
        break;
      case "provider":
        setEditableProvider(value);
        break;
      case "role":
        setEditableRole(value);
        break;
      case "description":
        setEditableDescription(value);
        break;
    }
  };

  const handleFullEdit = async () => {
    // Always pass the latest card as state for pre-population
    let cardForFullEdit = agentResponse || editAgentCard || editingAgentCard;
    if (
      editingAgentId &&
      (!Array.isArray(cardForFullEdit?.tools) || cardForFullEdit.tools.length === 0)
    ) {
      try {
        const agentDetails = await getAgent(editingAgentId);
        cardForFullEdit = {
          ...cardForFullEdit,
          ...agentDetails,
        };
        setHydratedEditingAgentCard(cardForFullEdit);
        setAgentResponse(cardForFullEdit);
      } catch {
        // Fall through with the best card already available.
      }
    }
    navigate("/agent/conversational", {
      state: { agentCard: cardForFullEdit },
    });
  };

  const handleFullAgentCreate = () => {
    // Always pass the latest card as state for pre-population
    navigate("/agent/conversational");
  };

  return (
    <Box sx={{ flex: 1, overflow: "auto", px: 3, pt: 2 }}>
      <Box>
        <Typography variant="h4">
          {isEdit ? "Edit Agent" : "Create Agent"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, px: 0.5 }}
        >
          {isEdit
            ? "Update your agent's metadata, knowledge collection, and tools."
            : "Provide your agent's intent to instantly draft its role and toolset."}
        </Typography>
        {!isEdit && (
          <Link
            component="button"
            onClick={handleFullAgentCreate}
            sx={{ ml: 0.5, fontWeight: 700, verticalAlign: "baseline" }}
          >
            Full Step-by-Step Agent Creator
          </Link>
        )}
        {isEdit && (
          <Link
            component="button"
            onClick={handleFullEdit}
            sx={{ ml: 0.5, fontWeight: 700, verticalAlign: "baseline" }}
          >
            Full Step-by-Step Editor
          </Link>
        )}
      </Box>

      {/* COMPACT STEPPER - Hide in edit mode */}
      {!isEdit && (
        <Box
          sx={{ mb: 4, mt: 3, display: "flex", justifyContent: "flex-start" }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{ width: "100%", maxWidth: "500px" }}
          >
            <Step>
              <StepLabel>Draft</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review</StepLabel>
            </Step>
          </Stepper>
        </Box>
      )}

      {/* STEP 1: DEFINE INTENT - Hide in edit mode */}
      {!isEdit && activeStep === 0 && (
        <Fade in={activeStep === 0}>
          <Box>
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2,
                mb: 2,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Card
                sx={{
                  p: 2,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, ml: 1, fontWeight: 700 }}
                >
                  Agent Intent
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="e.g. 'Generate image for a given prompt...'"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  variant="filled"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
              </Card>
            </Paper>
            <Box sx={{ mt: 4, display: "flex", justifyContent: "right" }}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                onClick={handleDraftIntent}
                disabled={isDrafting || !intent.trim()}
                startIcon={
                  isDrafting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    isEdit ?  <EditIcon /> : <AddCircleOutlineIcon />
                  )
                }
                sx={{
                  borderRadius: 99,
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  boxShadow: "0 10px 30px rgba(25, 118, 210, 0.3)",
                }}
              >
                {isDrafting ? "Drafting..." : "Draft"}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* STEP 2: REVIEW & LAUNCH - Always show in edit mode, otherwise show on step 1 */}
      {(isEdit || activeStep === 1) && (
        <Box sx={{ mt: isEdit ? 3 : 0 }}>
          <ExpressAgentReviewOrEdit
            identity={{
              name: editableAgentName,
              provider: editableProvider,
              role: editableRole,
              description: editableDescription,
            }}
            onIdentityChange={handleIdentityChange}
            knowledgeBase={{
              docIndexes,
              selectedDocIndex,
              selectedKnowledgeSpaceIds,
            }}
            onKnowledgeBaseChange={(ids) => {
              setSelectedKnowledgeSpaceIds(ids);
              setSelectedDocIndex(ids[0] || null);
            }}
            tools={{
              discoveredTools,
              availableKeys,
              onToolKeyChange: handleToolKeyChange,
            }}
            onCancel={handleCancelCreation}
            onCreate={handleCreateAgent}
            isCreating={isCreating}
            isEditMode={isEdit}
          />
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntentToAgentCreator;
